/**
 * MalwareAnalyzer design tokens (Cyble brand system). Single source of truth for color,
 * type, spacing, radius, border, and focus. This is the ONLY file in the app that may
 * contain raw hex values; every other surface reads from here (via the Tailwind preset,
 * which maps semantic names to CSS variables emitted per theme in styles.css, or by
 * importing these exports directly).
 *
 * MalwareAnalyzer is a product from Cyble (US-headquartered, YC W21).
 *
 * THEMES: the site ships six user-switchable palettes (light, dark, midnight, crimson,
 * graphite, terminal) plus a "system" auto choice. Invariants across every theme:
 *   - primary CTA and active-selection fills stay Cyble red (#C00000) with white text;
 *   - the focus ring stays 2px red;
 *   - verdict/severity colors stay red-family for malicious in every palette (including
 *     Terminal, whose interactive accent is phosphor green - the one sanctioned brand
 *     departure).
 * styles.css mirrors these values as CSS variables; tokens.test.ts asserts the mirror
 * never drifts and that text/surface pairs hold WCAG AA.
 */
/**
 * THE COLOUR RULE, and why it changed.
 *
 * Red used to do three jobs at once: primary call to action, focus ring, and the malicious verdict.
 * On a product whose entire job is telling you whether something is dangerous, that is a category
 * error. The button you are invited to press and the badge meaning "this will destroy your laptop"
 * cannot be the same colour, and while they were, nothing on the page could feel inviting and
 * nothing could feel genuinely alarming. Every interactive surface read as a warning.
 *
 * Now:
 *   primary  interactive only  buttons, links, tabs, selection, focus
 *   danger   threat only       malicious verdicts, destructive actions, errors
 *   cyble    attribution only  the Cyble wordmark and the Cyble Vision engine row
 *
 * `cyble` is deliberately separate from `danger` even though they are the same hex today. They mean
 * different things, and if the brand mark ever moves, a find-and-replace on the danger colour must
 * not drag it along.
 */
export const colors = {
    brand: {
        /** Interactive. The one colour that means "you can act on this". */
        primary: '#4F46E5',
        /** Threat and destruction. Never used for anything a user is invited to do. */
        danger: '#C00000',
        /** Cyble's own mark. Attribution surfaces only. */
        cyble: '#C00000',
        navy: '#050E2A',
    },
    neutral: { ink: '#4A4E59', micro: '#8A8F9A', surface: '#F5F5F7', canvas: '#FFFFFF' },
    verdict: { clean: '#2E7D32', suspicious: '#B45309', unknown: '#8A8F9A', malicious: '#C00000' },
};
/**
 * Motion. Small, physical, and honest about being interruptible.
 *
 * Durations are deliberately short: this is an instrument, and an instrument that makes you wait for
 * an animation is a slow instrument. `enter`/`exit` are asymmetric because things should appear
 * decisively and leave quietly. Every consumer must respect `prefers-reduced-motion`, which
 * styles.css already enforces globally.
 */
export const motion = {
    duration: { instant: 80, fast: 140, base: 200, slow: 320 },
    ease: {
        /** Decelerate: for anything entering. */
        out: 'cubic-bezier(0.16, 1, 0.3, 1)',
        /** Accelerate: for anything leaving. */
        in: 'cubic-bezier(0.4, 0, 1, 1)',
        /** Both: for anything moving between two on-screen positions. */
        inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
    },
};
/**
 * Three roles, not three decorative choices.
 *
 * `family` (Arial) stays the brand voice and carries all body and UI prose, so the reading
 * texture of the product is unchanged. `display` and `mono` are added because Arial fails
 * badly at exactly two jobs on this particular product:
 *
 *   - Headlines. Arial at a large size is the single loudest signal that nobody chose a
 *     typeface, and the homepage is the page we ask people to judge us by.
 *   - Numbers and hashes. `mono` previously ALIASED to Arial specifically so a second face
 *     could never sneak in, which meant all 237 `font-mono` usages across 44 files (every
 *     sha256, task id, command line and code block) rendered proportionally. On a malware
 *     analysis platform that is the wrong default.
 *
 * Geist and Geist Mono are SIL OFL, which keeps them inside the free-and-open licensing
 * rule the rest of the platform runs on, and they are self-hosted (see index.css) rather
 * than pulled from a font CDN. Both fall back through a metric-matched face so the swap
 * cannot shift the page.
 */
export const font = {
    /** Body and UI prose. The Cyble brand face; deliberately unchanged. */
    family: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
    /** Headings and display numerals. */
    display: '"Geist Variable", "Geist Fallback", Arial, "Helvetica Neue", Helvetica, sans-serif',
    /** Hashes, identifiers, code, and any tabular figure. */
    mono: '"Geist Mono Variable", "Geist Mono Fallback", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    weight: { bold: 700, semi: 600, medium: 500, regular: 400 },
    /**
     * `hero` and `section` exist to collapse the ladder rather than extend it. The old scale
     * (32/24/18/15/12) is the conventional five-step SaaS ramp, where each heading is a little
     * bigger than the last and nothing has any authority. The replacement carries hierarchy by
     * WEIGHT at one small size (`section`, 16/600) with a single large `hero`, so a page reads
     * as an instrument rather than a brochure. h1-h3 are retained because ~200 existing usages
     * depend on them and a forced rename would be churn, not design.
     */
    size: { hero: 56, h1: 32, h2: 24, h3: 18, section: 16, body: 15, micro: 12 },
    line: { header: 1.2, hero: 1.05, body: 1.5 },
    /** Headline tracking. Large type needs negative tracking to stop looking loose. */
    tracking: { hero: '-0.03em', tight: '-0.015em' },
};
export const space = { 1: 8, 2: 16, 3: 24, 4: 32, 5: 48 };
export const radius = { sm: 4, md: 8 };
export const border = '1px solid #F5F5F7';
export const focus = '2px solid #C00000';
/**
 * Sanctioned brand extensions for the data-viz palette and interaction states
 * (Phase 4: "Fourth color extends brand (#8B0000 or #1A2447), no new hues"). Kept here so
 * hex never leaks outside this file. darkRed doubles as the red hover/active state.
 */
export const brandExt = { darkRed: '#8B0000', darkNavy: '#1A2447' };
/** Verdict -> token hex. Used by badges, ribbons, and charts so the mapping stays consistent. */
export function verdictColor(v) {
    return colors.verdict[v];
}
// -------------------------------------------------------------------------------------------------
// Theme palettes
// -------------------------------------------------------------------------------------------------
export const THEME_NAMES = ['light', 'dark', 'midnight', 'crimson', 'graphite', 'terminal'];
export const themes = {
    light: {
        label: 'Light',
        swatch: { bg: '#F6F6F4', accent: '#C00000' },
        bg: { base: '#F6F6F4', raised: '#FFFFFF', overlay: '#FFFFFF', inset: '#EFEFED', surface: '#F6F6F4' },
        border: { subtle: '#EDEEF2', default: '#DDE0E7', strong: '#8A8F9A' },
        text: { primary: '#050E2A', secondary: '#4A4E59', muted: '#4A4E59', inverted: '#FFFFFF' },
        accent: { 50: '#EEEEFD', 300: '#7A78EE', 400: '#5B57E8', 500: '#4F46E5', 600: '#4338CA', 700: '#3730A3' },
        cta: { base: '#4F46E5', hover: '#4338CA', fg: '#FFFFFF' },
        status: { info: '#050E2A', success: '#2E7D32', warning: '#B45309', danger: '#C00000' },
        // unknown is #6B7280 (not neutral.micro) so badge text on white and white text on a
        // solid unknown fill both hold WCAG AA.
        verdict: { clean: '#2E7D32', suspicious: '#B45309', unknown: '#6B7280', malicious: '#C00000' },
        verdictFg: '#FFFFFF',
        chart: { primary: '#C00000', secondary: '#050E2A', tertiary: '#8A8F9A', quaternary: '#8B0000' },
        canvasDots: '#DDE0E7',
        focusRing: '#4F46E5',
        heat: '#C00000',
        shadow: { sm: '0 1px 2px rgba(5, 14, 42, 0.06)', overlay: '0 8px 24px rgba(5, 14, 42, 0.14)' },
    },
    dark: {
        label: 'Dark',
        swatch: { bg: '#08122E', accent: '#FF6B6B' },
        bg: { base: '#08122E', raised: '#0D1A3C', overlay: '#16264D', inset: '#040B20', surface: '#08122E' },
        border: { subtle: '#1B2540', default: '#2A3550', strong: '#3A4A73' },
        text: { primary: '#EDF1F9', secondary: '#ACB6CC', muted: '#8792AB', inverted: '#050E2A' },
        accent: { 50: '#1C2352', 300: '#B4B5FF', 400: '#9B9CFC', 500: '#9B9CFC', 600: '#7C7DEE', 700: '#6366D8' },
        cta: { base: '#4F46E5', hover: '#6366F1', fg: '#FFFFFF' },
        status: { info: '#8FA3D0', success: '#5CBF60', warning: '#F5A524', danger: '#FF6B6B' },
        verdict: { clean: '#5CBF60', suspicious: '#F5A524', unknown: '#97A1B5', malicious: '#FF6B6B' },
        verdictFg: '#08122E',
        chart: { primary: '#FF6B6B', secondary: '#8FA3D0', tertiary: '#97A1B5', quaternary: '#C00000' },
        canvasDots: '#22304F',
        focusRing: '#9B9CFC',
        heat: '#E03535',
        shadow: { sm: '0 1px 2px rgba(0, 0, 0, 0.4)', overlay: '0 12px 32px rgba(0, 0, 0, 0.55)' },
    },
    midnight: {
        label: 'Midnight',
        swatch: { bg: '#05070D', accent: '#FF6B6B' },
        bg: { base: '#05070D', raised: '#0B0E16', overlay: '#131826', inset: '#000000', surface: '#05070D' },
        border: { subtle: '#14181F', default: '#232833', strong: '#3A4150' },
        text: { primary: '#E9EDF5', secondary: '#A6AEBF', muted: '#808A9E', inverted: '#05070D' },
        accent: { 50: '#161A33', 300: '#B7B8FF', 400: '#9EA0FD', 500: '#9EA0FD', 600: '#7E80F0', 700: '#6568DA' },
        cta: { base: '#4F46E5', hover: '#6366F1', fg: '#FFFFFF' },
        status: { info: '#93A7D6', success: '#5CBF60', warning: '#F5A524', danger: '#FF6B6B' },
        verdict: { clean: '#5CBF60', suspicious: '#F5A524', unknown: '#8E99AD', malicious: '#FF6B6B' },
        verdictFg: '#05070D',
        chart: { primary: '#FF6B6B', secondary: '#93A7D6', tertiary: '#8E99AD', quaternary: '#C00000' },
        canvasDots: '#1C222E',
        focusRing: '#9EA0FD',
        heat: '#E03535',
        shadow: { sm: '0 1px 2px rgba(0, 0, 0, 0.5)', overlay: '0 12px 32px rgba(0, 0, 0, 0.65)' },
    },
    crimson: {
        label: 'Crimson',
        swatch: { bg: '#170A0D', accent: '#FF5C5C' },
        bg: { base: '#170A0D', raised: '#200D12', overlay: '#2B1218', inset: '#0E0507', surface: '#170A0D' },
        border: { subtle: '#241318', default: '#3A1D24', strong: '#57343C' },
        text: { primary: '#F7EDEE', secondary: '#D9C2C5', muted: '#C4A9AD', inverted: '#170A0D' },
        accent: { 50: '#2A1B3A', 300: '#C9B2FF', 400: '#B49BFB', 500: '#B49BFB', 600: '#9A7EF0', 700: '#8266DA' },
        cta: { base: '#4F46E5', hover: '#6366F1', fg: '#FFFFFF' },
        status: { info: '#D9C2C5', success: '#66BB6A', warning: '#F5A524', danger: '#FF5C5C' },
        verdict: { clean: '#66BB6A', suspicious: '#F5A524', unknown: '#B09CA0', malicious: '#FF5C5C' },
        verdictFg: '#170A0D',
        chart: { primary: '#FF5C5C', secondary: '#D9A7A7', tertiary: '#B09CA0', quaternary: '#8B0000' },
        canvasDots: '#33191F',
        focusRing: '#B49BFB',
        heat: '#E03535',
        shadow: { sm: '0 1px 2px rgba(0, 0, 0, 0.5)', overlay: '0 12px 32px rgba(0, 0, 0, 0.6)' },
    },
    graphite: {
        label: 'Graphite',
        swatch: { bg: '#131417', accent: '#FF6B6B' },
        bg: { base: '#131417', raised: '#1B1D21', overlay: '#24262B', inset: '#0C0D0F', surface: '#131417' },
        border: { subtle: '#202226', default: '#2E3136', strong: '#4A4E59' },
        text: { primary: '#ECEDEF', secondary: '#AEB2B9', muted: '#8A8F98', inverted: '#131417' },
        accent: { 50: '#20222B', 300: '#B6B8FF', 400: '#9D9FFA', 500: '#9D9FFA', 600: '#7E80EC', 700: '#6668D6' },
        cta: { base: '#4F46E5', hover: '#6366F1', fg: '#FFFFFF' },
        status: { info: '#AEB2B9', success: '#5CBF60', warning: '#F5A524', danger: '#FF6B6B' },
        verdict: { clean: '#5CBF60', suspicious: '#F5A524', unknown: '#9AA0A9', malicious: '#FF6B6B' },
        verdictFg: '#131417',
        chart: { primary: '#FF6B6B', secondary: '#AEB2B9', tertiary: '#8A8F98', quaternary: '#C00000' },
        canvasDots: '#26282E',
        focusRing: '#9D9FFA',
        heat: '#E03535',
        shadow: { sm: '0 1px 2px rgba(0, 0, 0, 0.45)', overlay: '0 12px 32px rgba(0, 0, 0, 0.55)' },
    },
    terminal: {
        label: 'Terminal',
        swatch: { bg: '#060B06', accent: '#3FBF3F' },
        bg: { base: '#060B06', raised: '#0A140A', overlay: '#102010', inset: '#030703', surface: '#060B06' },
        border: { subtle: '#142314', default: '#1D331D', strong: '#2F4F2F' },
        text: { primary: '#D7F5D7', secondary: '#93C793', muted: '#6FA06F', inverted: '#060B06' },
        accent: { 50: '#102010', 300: '#7FE07F', 400: '#5FD35F', 500: '#5FD35F', 600: '#3FBF3F', 700: '#2FA32F' },
        cta: { base: '#4F46E5', hover: '#6366F1', fg: '#FFFFFF' },
        status: { info: '#93C793', success: '#58D058', warning: '#F5A524', danger: '#FF5C5C' },
        verdict: { clean: '#58D058', suspicious: '#F5A524', unknown: '#86A886', malicious: '#FF5C5C' },
        verdictFg: '#060B06',
        chart: { primary: '#3FBF3F', secondary: '#93C793', tertiary: '#6FA06F', quaternary: '#FF5C5C' },
        canvasDots: '#163016',
        focusRing: '#5FD35F',
        heat: '#E03535',
        shadow: { sm: '0 1px 2px rgba(0, 0, 0, 0.5)', overlay: '0 12px 32px rgba(0, 0, 0, 0.6)' },
    },
};
/** Hex "#RRGGBB" -> "R G B" triplet for rgb(var(--x) / alpha) CSS variables. */
export function hexToTriplet(hex) {
    const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m || !m[1])
        throw new Error(`hexToTriplet: not a 6-digit hex color: ${hex}`);
    const n = parseInt(m[1], 16);
    return `${(n >> 16) & 0xff} ${(n >> 8) & 0xff} ${n & 0xff}`;
}
/**
 * Flatten a theme palette into the CSS variables emitted in styles.css (color values as
 * "R G B" triplets; shadows verbatim). tokens.test.ts asserts styles.css matches this
 * exactly, so tokens.ts stays the single source of truth.
 */
export function themeCssVars(name) {
    const t = themes[name];
    return {
        '--tl-bg-base': hexToTriplet(t.bg.base),
        '--tl-bg-raised': hexToTriplet(t.bg.raised),
        '--tl-bg-overlay': hexToTriplet(t.bg.overlay),
        '--tl-bg-inset': hexToTriplet(t.bg.inset),
        '--tl-bg-surface': hexToTriplet(t.bg.surface),
        '--tl-border-subtle': hexToTriplet(t.border.subtle),
        '--tl-border-default': hexToTriplet(t.border.default),
        '--tl-border-strong': hexToTriplet(t.border.strong),
        '--tl-text-primary': hexToTriplet(t.text.primary),
        '--tl-text-secondary': hexToTriplet(t.text.secondary),
        '--tl-text-muted': hexToTriplet(t.text.muted),
        '--tl-text-inverted': hexToTriplet(t.text.inverted),
        '--tl-accent-50': hexToTriplet(t.accent[50]),
        '--tl-accent-300': hexToTriplet(t.accent[300]),
        '--tl-accent-400': hexToTriplet(t.accent[400]),
        '--tl-accent-500': hexToTriplet(t.accent[500]),
        '--tl-accent-600': hexToTriplet(t.accent[600]),
        '--tl-accent-700': hexToTriplet(t.accent[700]),
        '--tl-cta': hexToTriplet(t.cta.base),
        '--tl-cta-hover': hexToTriplet(t.cta.hover),
        '--tl-cta-fg': hexToTriplet(t.cta.fg),
        '--tl-status-info': hexToTriplet(t.status.info),
        '--tl-status-success': hexToTriplet(t.status.success),
        '--tl-status-warning': hexToTriplet(t.status.warning),
        '--tl-status-danger': hexToTriplet(t.status.danger),
        '--tl-verdict-clean': hexToTriplet(t.verdict.clean),
        '--tl-verdict-suspicious': hexToTriplet(t.verdict.suspicious),
        '--tl-verdict-unknown': hexToTriplet(t.verdict.unknown),
        '--tl-verdict-malicious': hexToTriplet(t.verdict.malicious),
        '--tl-verdict-fg': hexToTriplet(t.verdictFg),
        '--tl-chart-primary': hexToTriplet(t.chart.primary),
        '--tl-chart-secondary': hexToTriplet(t.chart.secondary),
        '--tl-chart-tertiary': hexToTriplet(t.chart.tertiary),
        '--tl-chart-quaternary': hexToTriplet(t.chart.quaternary),
        '--tl-canvas-dots': hexToTriplet(t.canvasDots),
        '--tl-focus-ring': hexToTriplet(t.focusRing),
        '--tl-heat': hexToTriplet(t.heat),
        '--tl-shadow-sm': t.shadow.sm,
        '--tl-shadow-overlay': t.shadow.overlay,
    };
}
export const tokens = { colors, font, space, radius, border, focus, brandExt, themes };
//# sourceMappingURL=tokens.js.map